import { Modal } from 'almanac-ui/Modal'
import WordPressStyles from './WordPress.module.scss'
import { Form } from 'components/Wizards/Form/Form'
import { Formik } from 'formik'
import * as yup from 'yup'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Field } from 'components/Wizards/Field/Field'
import { Text } from 'almanac-ui/Text/Text'
import { useMutation } from 'react-query'
import { WordPressClient } from 'api/clients/WordPressClient'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showToastMessage, showIntegrationSuccessToast } from 'common/ToastMessage/ShowToastMessage'
import { DismissToast } from 'common/ToastMessage'
import { useRef, useState } from 'react'
import { Heading } from 'blaze-ui/Heading/Heading'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { BLAZE_WORDPRESS_PLUGIN_HELP_CENTER_URL } from 'paths'

interface ConnectWordPressModalProps {
  onSuccessfulConnect: () => void
  onCancel?: () => void
  initialValues?: {
    url: string
    username: string
  }
}

const schema = yup.object().shape({
  url: yup
    .string()
    .required()
    .url()
    .test('no-wp-admin', 'URL should not include /wp-admin/', (value) => !value.includes('wp-admin')),
  username: yup.string().required(),
  password: yup.string().required(),
})

type FormValues = yup.InferType<typeof schema>

export const ConnectWordPressModal = ({
  onSuccessfulConnect,
  onCancel,
  initialValues = { url: '', username: '' },
}: ConnectWordPressModalProps) => {
  const workspaceId = useCurrentWorkspaceId()
  const loadingMessageRef = useRef<DismissToast | undefined>()
  const { isMobile } = useResponsiveDesign()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const createIntegration = useMutation(WordPressClient.createIntegration, {
    onSuccess: () => {
      loadingMessageRef.current?.()
      showIntegrationSuccessToast('Successfully connected to WordPress!')
      onSuccessfulConnect()
    },
    onMutate: () => {
      loadingMessageRef.current = showToastMessage('Connecting with WordPress...', 'loading')
    },
    onError: (e: Error) => {
      loadingMessageRef.current?.()
      showToastMessage('Unable to connect to WordPress', 'error')
      setErrorMessage(e.message)
    },
  })

  const handleSubmit = async (values: FormValues) => {
    createIntegration.mutate({ workspaceId, params: values })
  }

  const form = () => (
    <Formik initialValues={{ ...initialValues, password: '' }} validationSchema={schema} onSubmit={handleSubmit}>
      {({ values, touched, errors, submitForm, setFieldValue }) => (
        <div className={WordPressStyles.modalContent}>
          <div className={WordPressStyles.header}>
            <Heading level={3}>Allow Blaze to access your WordPress account</Heading>
          </div>
          <Form.Root>
            <div className={WordPressStyles.form}>
              <span className={WordPressStyles.instructions}>
                First, install the{' '}
                <a href={BLAZE_WORDPRESS_PLUGIN_HELP_CENTER_URL} target="_blank" rel="noreferrer">
                  Blaze WordPress plugin
                </a>
                <span> to enable the connection.</span>
              </span>
              {errorMessage && <div className={WordPressStyles.errorContainer}>{errorMessage}</div>}
              <div className={WordPressStyles.fieldWithDescription}>
                <Field label="Base URL" isRequired>
                  <Input
                    size="lg"
                    name="url"
                    value={values.url}
                    errorMessage={touched.url && errors.url}
                    placeholder="https://my-wordpress-site.com"
                    onChange={(e) => setFieldValue('url', e.target.value)}
                  />
                </Field>
                <Text variant="p1" color="var(--neutral-50)">
                  Enter your full publicly accessible WordPress URL with leading http:// or https://. Do{' '}
                  <span className={WordPressStyles.bold}>not</span> include <kbd>/wp-admin/</kbd> or{' '}
                  <kbd>/wp-login/</kbd>
                  <br />
                  <br />
                  If you have a <span className={WordPressStyles.bold}>custom login URL</span> please do not include the
                  custom URL value. For example: use <kbd>https://example.com</kbd> instead of{' '}
                  <kbd>https://example.com/login</kbd> as your Base URL
                </Text>
              </div>
              <Field label="Username" isRequired>
                <Input
                  size="lg"
                  name="username"
                  value={values.username}
                  errorMessage={touched.username && errors.username}
                  placeholder="Username you use to login to WordPress"
                  onChange={(e) => setFieldValue('username', e.target.value)}
                />
              </Field>
              <Field label="Application Password" isRequired>
                <Input
                  size="lg"
                  type="password"
                  name="password"
                  value={values.password}
                  errorMessage={touched.password && errors.password}
                  placeholder="Application password you created for Blaze"
                  onChange={(e) => setFieldValue('password', e.target.value)}
                />
              </Field>
              <Text variant="p2" color="var(--neutral-50)">
                <br />
                If you are using WordPress 5.6 or higher, you should use an{' '}
                <a href="https://make.wordpress.org/core/2020/11/05/application-passwords-integration-guide/">
                  Application Password
                </a>{' '}
                <span> for your password, otherwise you may experience issues authenticating.</span>
              </Text>
            </div>
            <div className={WordPressStyles.footer}>
              {onCancel && (
                <Modal.FooterButton variant="tertiary" size="lg" onPress={onCancel}>
                  Back
                </Modal.FooterButton>
              )}
              <Modal.FooterButton variant={'primary'} size={'lg'} onPress={submitForm}>
                Connect to WordPress
              </Modal.FooterButton>
            </div>
          </Form.Root>
        </div>
      )}
    </Formik>
  )

  if (isMobile) return <>{form()}</>

  return (
    <Modal.Root size="fullscreen">
      <Modal.Top>
        <Modal.BackButton onPress={onCancel} />
      </Modal.Top>
      {form()}
    </Modal.Root>
  )
}
